
export interface ViewModel {
  status: Status
  messages: Messages
  nextPage: NextPage
  alertSection?: AlertSection
}
export interface Status {
  label: string
  icon: 'success' | 'cancel' | 'error'
}
export interface Messages {
  info: string[]
  error: string[]
}
export interface NextPage {
  path: string
  buttonLabel: string
  autoRedirect: boolean
  requireLogin: boolean
  buttonMessage?: string
}
export interface AlertSection {
  title: string
  messages: string[]
}

const paymentSuccessfullyDoneStatus: Status = {
  label: '決済が完了しました',
  icon: 'success',
}
const paymentErrorStatus: Status = {
  label: '決済エラー',
  icon: 'error',
}
const paymentCanceledStatus: Status = {
  label: '決済が中断されました',
  icon: 'cancel',
}
/**
 * パラメータ不正や予期せぬエラーなどの場合に利用される
 */
const unexpectedErrorStatus: Status = {
  label: '決済が失敗した可能性があります',
  icon: 'error',
}

const orderCompleteNextPage: Omit<NextPage, 'path'> = {
  buttonLabel: '注文内容を確認する',
  autoRedirect: true,
  requireLogin: false,
}
const orderHistoryNextPage: NextPage = {
  path: '/s/mypage/order',
  buttonLabel: '注文内容を確認する',
  autoRedirect: false,
  requireLogin: false,
}
const orderHistoryViaLoginNextPage: NextPage = {
  path: '/s/login?next=/s/mypage/order',
  buttonLabel: 'ログインする',
  autoRedirect: false,
  requireLogin: true,
  buttonMessage: 'ご注文内容の確認にはログインが必要です',
}
const reorderNextPage: NextPage = {
  path: '/s/mypage/order',
  buttonLabel: '決済を再開する',
  autoRedirect: false,
  requireLogin: false,
}
const reorderViaLoginNextPage: NextPage = {
  path: '/s/login?next=/s/mypage/order',
  buttonLabel: 'ログインする',
  autoRedirect: false,
  requireLogin: true,
  buttonMessage: '決済を再開するにはログインが必要です',
}

const checkOrderHistoryAlertSection: AlertSection = {
  title: '必ずご確認ください',
  messages: [
    'マイページ＞ご購入履歴をご確認ください。',
    '「注文完了（未発送）」や「出荷準備中」と表示されている場合は、正常に決済が完了しております。「決済未完了」となっている場合は決済が正常に完了していない可能性があります。',
    'しばらくお時間をおいても「決済未完了」からステータスが更新されない場合、各種決済も順次自動キャンセルされますのでご安心ください。',
  ],
}
const reorderAlertSection: AlertSection = {
  title: '決済を再開するには',
  messages: [
    'マイページ＞ご購入履歴からご注文の決済を再開していただけます。',
    '1時間以上決済が確認できない場合は、ご注文を自動でキャンセルさせていただきます。',
  ],
}
/**
 * 外部決済からリダイレクトされないことがあるため、
 * 暫定的にキャンセル画面に注文完了の可能性があるメッセージを追加している
 */
const reorderOnCancelAlertSection: AlertSection = {
  title: '決済を再開するには',
  messages: [
    'マイページ＞ご購入履歴からご注文の決済を再開していただけます。',
    '1時間以上決済が確認できない場合は、ご注文を自動でキャンセルさせていただきます。',
    '「注文完了（未発送）」や「出荷準備中」と表示されている場合は、正常に決済が完了しております。',
  ],
}

export abstract class BaseViewModelBuilder<T extends BaseViewModelBuilder<T>> {
  protected constructor(
    protected readonly status: Status,
    protected readonly infoMessages: string[],
    protected readonly errorMessages: string[],
    protected readonly alertSection?: AlertSection
  ) {}

  protected addInfoMessage(message: string): T {
    return this.innerConstructor(
      this.status,
      [...this.infoMessages, message],
      this.errorMessages,
      this.alertSection
    )
  }
  protected addErrorMessage(message: string): T {
    return this.innerConstructor(
      this.status,
      this.infoMessages,
      [...this.errorMessages, message],
      this.alertSection
    )
  }
  protected withAlertSection(alertSection: AlertSection): T {
    return this.innerConstructor(
      this.status,
      this.infoMessages,
      this.errorMessages,
      alertSection
    )
  }
  protected buildWithNextPage(nextPage: NextPage): ViewModel {
    return {
      status: this.status,
      messages: {
        info: this.infoMessages,
        error: this.errorMessages,
      },
      nextPage,
      alertSection: this.alertSection,
    }
  }

  protected abstract innerConstructor(
    status: Status,
    infoMessages: string[],
    errorMessages: string[],
    alertSection?: AlertSection
  ): T
}

export class DefaultViewModelBuilder extends BaseViewModelBuilder<DefaultViewModelBuilder> {
  static forPaymentSuccessfullyDone(): DefaultViewModelBuilder {
    return DefaultViewModelBuilder.createWithStatus(paymentSuccessfullyDoneStatus)
  }
  static forPaymentError(): DefaultViewModelBuilder {
    return DefaultViewModelBuilder.createWithStatus(paymentErrorStatus)
  }
  static forPaymentCanceled(): DefaultViewModelBuilder {
    return DefaultViewModelBuilder.createWithStatus(paymentCanceledStatus)
  }
  static forUnexpectedError(): DefaultViewModelBuilder {
    return DefaultViewModelBuilder.createWithStatus(unexpectedErrorStatus)
  }
  private static createWithStatus(status: Status): DefaultViewModelBuilder {
    return new DefaultViewModelBuilder(status, [], [], undefined)
  }

  addGuideToOrderCompleteInfoMessage(): DefaultViewModelBuilder {
    return this.addInfoMessage(
      'ご注文内容をご確認いただけるページを表示します。'
    )
  }
  addCheckOrderHistoryErrorMessage(): DefaultViewModelBuilder {
    return this.addErrorMessage(
      'ご注文内容は購入履歴をご確認ください。'
    )
  }
  addMaybeErrorErrorMessage(): DefaultViewModelBuilder {
    return this.addErrorMessage(
      '注文が正常に完了しなかった可能性があります。ご注文履歴をご確認ください。'
    )
  }
  addGuideToOrderHistoryErrorMessage(): DefaultViewModelBuilder {
    return this.addErrorMessage(
      'お手数ですが、念のためご購入履歴をご確認ください。'
    )
  }
  addGuideToReorderErrorMessage(): DefaultViewModelBuilder {
    return this.addErrorMessage(
      '決済処理でエラーが発生しました。お手数ですが注文履歴をご確認の上、決済手続きを再開してください。'
    )
  }

  withCheckOrderHistoryAlertSection(): DefaultViewModelBuilder {
    return this.withAlertSection(checkOrderHistoryAlertSection)
  }
  withReorderAlertSection(): DefaultViewModelBuilder {
    return this.withAlertSection(reorderAlertSection)
  }
  withReorderAlertSectionOnCancel(): DefaultViewModelBuilder {
    return this.withAlertSection(reorderOnCancelAlertSection)
  }

  buildWithOrderCompleteLink(orderCompletePath: string): ViewModel {
    return this.buildWithNextPage({
      path: orderCompletePath,
      ...orderCompleteNextPage,
    })
  }
  buildWithOrderHistoryLink(): ViewModel {
    return this.buildWithNextPage(orderHistoryNextPage)
  }
  buildWithOrderHistoryViaLoginLink(): ViewModel {
    return this.buildWithNextPage(orderHistoryViaLoginNextPage)
  }
  buildWithReorderLink(): ViewModel {
    return this.buildWithNextPage(reorderNextPage)
  }
  buildWithReorderViaLoginLink(): ViewModel {
    return this.buildWithNextPage(reorderViaLoginNextPage)
  }

  protected innerConstructor(
    status: Status,
    infoMessages: string[],
    errorMessages: string[],
    alertSection?: AlertSection
  ): DefaultViewModelBuilder {
    return new DefaultViewModelBuilder(status, infoMessages, errorMessages, alertSection)
  }
}

/**
 * @internal 同じディレクトリの別ファイルから参照するため
 */
export const internalExported = {
  paymentSuccessfullyDoneStatus,
  paymentErrorStatus,
  paymentCanceledStatus,
  unexpectedErrorStatus,
  checkOrderHistoryAlertSection,
  reorderAlertSection,
  reorderOnCancelAlertSection,
}
